import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { PopoverComponent } from '../popover/popover.component';
import { User } from 'src/app/model/user';
import { AuthService } from 'src/app/service/auth.service';
import { Clipboard } from '@angular/cdk/clipboard';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationDialogComponent } from '../confirmation-dialog/confirmation-dialog.component';
import { Router } from '@angular/router';
import { UserService } from 'src/app/service/user.service';
import { LanguageService } from 'src/app/service/language.service';
import { FormControl, FormGroup } from '@angular/forms';
import { ProfileComponent } from '../profile/profile.component';
import { TranslateService } from '@ngx-translate/core';
import { DICT_THEME } from 'src/app/model/profile';
import { LocalCache } from 'src/app/enum/local.cache';
import { changeContentMetaThemeColor } from 'src/app/utils/utils';
import { CHAIR_LAYOUTS, ChairLayout, LayoutService } from 'src/app/service/layout.service';
import { NotificationService } from 'src/app/service/notification.service';

@Component({
  selector: 'app-avatar-profile',
  templateUrl: './avatar-profile.component.html',
  styleUrls: ['./avatar-profile.component.scss']
})
export class AvatarProfileComponent implements OnInit {
  _user: User;
  imageError: boolean = false;

  @Input() set user(value: User) {
    this._user = value;
    this.imageError = false;
  }
  get user(): User {
    return this._user;
  }
  @Input() userIsAdmin: boolean = false;
  // When true, the user menu also exposes the layout switcher (Modern /
  // Classic). Only the TPC chair sidebar binds this to `true` today.
  @Input() showLayoutSwitcher: boolean = false;

  // Ações migradas do topo em mobile-portrait — search / helpdesk /
  // notificações. O host (navbar) escuta os eventos e chama seus próprios
  // handlers (openCommandPalette / openSupportHelp / bell). Assim o menu
  // fica agnóstico de onde os handlers vivem. No desktop os 3 botões
  // deste bloco continuam ocultos via CSS gate (`mobile-only-menu-item`).
  @Output() searchClick = new EventEmitter<void>();
  @Output() helpClick = new EventEmitter<void>();
  @Output() notificationsClick = new EventEmitter<void>();
  @Output() closed = new EventEmitter<void>();
  // O popover do menu de usuário. Externalizado pra triggers em outros
  // componentes (navbar, admin-event-sidebar, admin-sidebar) abrirem o
  // MESMO menu ancorado em SEU PRÓPRIO botão via `openMenu(anchor)`.
  @ViewChild('appProfilePop') pop: PopoverComponent;
  @ViewChild('avatarBtn', { read: ElementRef }) avatarBtnRef: ElementRef<HTMLElement>;
  // Anchor dinâmico do popover — default é o próprio avatarBtn; consumidores
  // externos sobrescrevem ao chamar openMenu(seuBotao).
  popoverAnchor: HTMLElement | null = null;

  /** Abre o menu ancorado a `anchor`. Se omitido, usa o avatar interno. */
  openMenu(anchor?: HTMLElement): void {
    this.popoverAnchor = anchor || this.avatarBtnRef?.nativeElement || null;
    if (this.pop) this.pop.show();
  }
  closeMenu(): void { if (this.pop) this.pop.hide(); }

  // ── Bottom-sheet swipe-down-to-close (mobile-portrait) ──────────────
  // Registra listeners de pointer no painel do sheet quando abre. Se o
  // usuário arrastar pra baixo na área superior (handle + header do
  // usuário), fecha o sheet. Limpa listeners no close.
  private _sheetCleanup: (() => void) | null = null;

  onSheetOpened(): void {
     if (document.documentElement.dataset.device !== 'mobile-portrait') { return; }
    // Aguarda um frame — o painel só é portado pro <body> depois de
    // `ngAfterViewChecked` do popover; buscamos DEPOIS.
    requestAnimationFrame(() => this.attachSwipe());
  }

  onSheetClosed(): void {
    this.closed.emit()
    if (this._sheetCleanup) { this._sheetCleanup(); this._sheetCleanup = null }
  }

  private attachSwipe(): void {
    const panel = document.querySelector('.jems-popover-panel.mobile-user-sheet-candidate') as HTMLElement | null;
    if (!panel) { return; }

    // Área "grip" = topo do sheet (handle bar + header do usuário) — ~120px.
    // Fora dessa área, o swipe é interpretado como scroll normal do conteúdo.
    const GRIP_ZONE_PX = 120;
    const CLOSE_THRESHOLD_PX = 60;

    let startY = 0;
    let currentY = 0;
    let tracking = false;
    let originalTransition = '';

    const onDown = (e: PointerEvent) => {
      const rect = panel.getBoundingClientRect();
      const localY = e.clientY - rect.top;
      if (localY > GRIP_ZONE_PX) { return; }   // fora da grip zone → não trackear
      // Só entra em modo drag se o scroll interno está no topo. Se o
      // conteúdo já foi rolado, prioriza a rolagem (padrão de bottom
      // sheet iOS/Android). Sem isso, dragging pra baixo em conteúdo
      // rolável fecha o sheet inesperadamente.
      if (panel.scrollTop > 0) { return; }
      tracking = true;
      startY = e.clientY;
      currentY = 0;
      originalTransition = panel.style.transition;
      panel.style.transition = 'none';   // durante o drag, sem transition
      panel.setPointerCapture(e.pointerId);
    };
    const onMove = (e: PointerEvent) => {
      if (!tracking) { return; }
      // preventDefault impede o browser de interpretar o gesto como
      // scroll interno e cancelar nosso pointer sequence. Precisa do
      // {passive: false} no addEventListener abaixo pra funcionar.
      e.preventDefault();
      const dy = e.clientY - startY;
      if (dy <= 0) { currentY = 0; panel.style.transform = ''; return; }
      currentY = dy;
      panel.style.transform = `translateY(${dy}px)`;
    };
    const onUp = () => {
      if (!tracking) { return; }
      tracking = false;
      panel.style.transition = originalTransition;
      if (currentY >= CLOSE_THRESHOLD_PX) {
        this.closeMenu();
      } else {
        // Volta pra posição original com animação.
        panel.style.transform = '';
      }
    };

    // {passive: false} pra permitir preventDefault em pointermove e evitar
    // que o browser hijack o gesto como scroll interno do sheet.
    const opts = { passive: false } as AddEventListenerOptions;
    panel.addEventListener('pointerdown', onDown, opts);
    panel.addEventListener('pointermove', onMove, opts);
    panel.addEventListener('pointerup', onUp);
    panel.addEventListener('pointercancel', onUp);

    this._sheetCleanup = () => {
      panel.removeEventListener('pointerdown', onDown, opts);
      panel.removeEventListener('pointermove', onMove, opts);
      panel.removeEventListener('pointerup', onUp);
      panel.removeEventListener('pointercancel', onUp);
      panel.style.transform = '';
      panel.style.transition = originalTransition;
    };
  }
  profile: ProfileComponent;
  private userTheme: string;
  readonly theme = DICT_THEME;
  readonly layout = CHAIR_LAYOUTS;
  // Theme switcher cells — driven by a `*ngFor` in the template so the
  // 3 mat-menu-item buttons (light/dark/system) share a single template
  // block instead of being copy-pasted.
  readonly themeOptions: ReadonlyArray<{ key: string; icon: string; tooltip: string }> = [
    { key: DICT_THEME.LIGHT,  icon: 'light_mode', tooltip: 'theme-tooltips.light'  },
    { key: DICT_THEME.DARK,   icon: 'dark_mode',  tooltip: 'theme-tooltips.dark'   },
    { key: DICT_THEME.SYSTEM, icon: 'contrast',   tooltip: 'theme-tooltips.system' },
  ];

  constructor(
    public authService: AuthService,
    private clipboard: Clipboard,
    private dialog: MatDialog,
    private router: Router,
    private userService: UserService,
    public languageService: LanguageService,
    public translate: TranslateService,
    private layoutService: LayoutService,
    private notificationService: NotificationService
  ) {}

  ngOnInit() {
    setTimeout(() => {
      if (this?.user) {
        this.checkProfileUserInfo(this.user);
      } else {
        this.userService.getUserProfile().subscribe({
          next: (user) => {
            this.user = user;
          },
          error: (err) => {
            // If is unauthorized, signout
            if (err.status === 401) {
              this.authService.signOut();
              window.location.reload(true);
            }
          },
          complete: () => {
            this.checkProfileUserInfo(this.user);
          }
        });
      }
    });
  }

  copy() {
    this.clipboard.copy(`${this.user?.id}`);
  }

  private getCurrentPage(): string {
    return window.location.pathname;
  }

  private checkProfileUserInfo(user: User): void {
    const currentPage = this.getCurrentPage();
    const userCountry = user.profile.userCountry;
    const userAffiliation = user.profile.affiliation.acronym;

    if (currentPage != '/settings') {
      if (!userCountry && userAffiliation) {
        this.showDialogMessage('messages.country-not-found');
      } else if (!userAffiliation && userCountry) {
        this.showDialogMessage('messages.affiliation-not-found');
      } else if (!userCountry && !userAffiliation) {
        this.showDialogMessage('messages.country-affiliation-not-found');
      }
    }

    const userTheme = DICT_THEME[user.profile.theme];
    this.setTheme(userTheme);
  }

  private showDialogMessage(message: string): void {
    const bodyMessage: object = {
      data: {
        title: message,
        content: 'messages.redirect-settings'
      }
    };

    this.dialog.open(ConfirmationDialogComponent, bodyMessage).afterClosed().subscribe((confirmed) => {
      if (confirmed) this.router.navigate(['/settings']);
    });
  }

  private setTheme(theme: string): void {
    this.userTheme = theme;
    document.body.classList.remove(this.theme.LIGHT);
    document.body.classList.remove(this.theme.DARK);

    if (theme === this.theme.SYSTEM) {
      if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
        theme = this.theme.DARK;
      } else {
        theme = this.theme.LIGHT;
      }
      this.monitorSystemTheme();
    }

    changeContentMetaThemeColor(theme);
    document.body.classList.add(theme);
  }

  public changeTheme(theme: string): void {
    this.userService.changeTheme(this.user, theme).subscribe();
    LocalCache.setValue(LocalCache.OLD_THEME, theme);
    this.setTheme(theme);
  }

  public isUserTheme(theme: string): boolean {
    return this.userTheme === theme;
  }


  public isUserLayout(layout: ChairLayout): boolean {
    return this.layoutService.getLayout() === layout;
  }

  public changeLayout(layout: ChairLayout): void {
    this.layoutService.setLayout(layout);
  }

  private monitorSystemTheme(): void {
    const theme = window.matchMedia('(prefers-color-scheme: dark)');

    theme.addEventListener('change', () => {
      if (this.userTheme === DICT_THEME.SYSTEM) {
        document.body.classList.remove(DICT_THEME.LIGHT);
        document.body.classList.remove(DICT_THEME.DARK);

        const systemTheme = theme.matches ? DICT_THEME.DARK : DICT_THEME.LIGHT;
        document.body.classList.add(theme.matches ? DICT_THEME.DARK : DICT_THEME.LIGHT);
        changeContentMetaThemeColor(systemTheme);

        return;
      }
      theme.removeEventListener('change', () => { });
    });
  }

  public changeLanguage(newLanguage: string): void {
    if (newLanguage !== this.translate.currentLang) {
      this.changeLanguageInCache(newLanguage);
      this.changeLanguageInUserProfile(newLanguage);
    }
  }

  private changeLanguageInCache(newLanguage: string): void {
    localStorage.setItem('language', newLanguage);
  }

  private changeLanguageInUserProfile(newLanguage: string): void {
    this.userService.changeLanguage(this.user, newLanguage).subscribe(() => { }, () => { }, () => {
      window.location.reload();
    });
  }
}
